// User utility functions

import { userLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

/**
 * Get badge color class for user type
 */
export const getUserTypeBadgeColor = (userType: string): string => {
  switch (userType) {
    case "individual":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "dealer":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "business":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
    case "admin":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
};

/**
 * Get display label for user type
 */
export const getUserTypeLabel = (userType: string): string => {
  const labels = userLabels(getLanguage());
  const userTypes = labels.userTypes || {};
  
  switch (userType) {
    case "individual":
      return userTypes.individual;
    case "dealer":
      return userTypes.dealer;
    case "business":
      return userTypes.business;
    case "admin":
      return userTypes.admin;
    default:
      return userTypes.unknown;
  }
};

/**
 * Get plural display label for user type
 */
export const getUserTypeLabelPlural = (userType: string): string => {
  const labels = userLabels(getLanguage());
  const userTypesPlural = labels.userTypesPlural || {};
  
  switch (userType) {
    case "individual":
      return userTypesPlural.individual;
    case "dealer":
      return userTypesPlural.dealer;
    case "business":
      return userTypesPlural.business;
    case "admin":
      return userTypesPlural.admin;
    default:
      return userTypesPlural.default;
  }
};

// Registration request utilities

/**
 * Get badge color class for registration request status
 * Accepts both lowercase ("pending") and uppercase ("PENDING") enum values
 * Handles undefined/null values gracefully
 */
export const getStatusBadgeColor = (status: string | undefined | null): string => {
  if (!status) {
    return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
  const normalizedStatus = status.toUpperCase();
  switch (normalizedStatus) {
    case "PENDING":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "APPROVED":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "REJECTED":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
};

/**
 * Get display label for registration request status
 * Accepts both lowercase ("pending") and uppercase ("PENDING") enum values
 * Handles undefined/null values gracefully
 */
export const getStatusLabel = (status: string | undefined | null): string => {
  if (!status) {
    return "Okänd";
  }
  const labels = userLabels(getLanguage());
  const statusLabels = labels.status || {};
  const normalizedStatus = status.toUpperCase();
  
  switch (normalizedStatus) {
    case "PENDING":
      return statusLabels.pending;
    case "APPROVED":
      return statusLabels.approved;
    case "REJECTED":
      return statusLabels.rejected;
    default:
      return status;
  }
};

/**
 * Get display label for registration request user type
 * Accepts both lowercase ("dealer") and uppercase ("DEALER") enum values
 */
export const getRegistrationUserTypeLabel = (userType: string): string => {
  const labels = userLabels(getLanguage());
  const registrationUserTypes = labels.registrationUserTypes || {};
  const normalizedUserType = userType.toUpperCase();
  
  switch (normalizedUserType) {
    case "DEALER":
      return registrationUserTypes.dealer;
    case "BUSINESS":
      return registrationUserTypes.business;
    default:
      return userType;
  }
};
